import { useState } from "react";

function CheckIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-green-500"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <circle cx="12" cy="12" r="12" className="text-green-500" fill="currentColor" opacity="0.15" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.03 8.47a.75.75 0 0 1 0 1.06l-5.5 5.5a.75.75 0 0 1-1.06 0l-2.5-2.5a.75.75 0 1 1 1.06-1.06L11 13.44l4.97-4.97a.75.75 0 0 1 1.06 0z"
        fill="currentColor"
      />
    </svg>
  );
}

function EmptyCircleIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-text-muted"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

export default function WelcomeChecklist({
  bandId,
  songs,
  setlists,
  members,
  canInvite,
  hasSongOpened,
  onAddSongs,
  onHighlightAdd,
  onHighlightSongRow,
  onGoToMembers,
}) {
  const storageKey = `bos_checklist_dismissed_${bandId}`;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(storageKey) === "true"
  );

  if (dismissed) return null;

  const steps = [
    {
      label: "Add your songs",
      complete: songs.length >= 1,
      ctaLabel: "Add songs",
      onCta: onAddSongs,
    },
    {
      label: "Tap a song to open it",
      complete: hasSongOpened,
      ctaLabel: "Tap any song row to view sheet music",
      onCta: songs.length > 0 ? onHighlightSongRow : null,
    },
    {
      label: "Build a setlist",
      complete: setlists.length >= 1,
      ctaLabel: songs.length > 0 ? "Click 'Add' on any song below" : null,
      onCta: songs.length > 0 ? onHighlightAdd : null,
    },
    ...(canInvite
      ? [
          {
            label: "Invite your band",
            complete: members.length >= 2,
            ctaLabel: "Go to Members",
            onCta: onGoToMembers,
          },
        ]
      : []),
  ];

  const totalSteps = steps.length;
  const completedCount = steps.filter((s) => s.complete).length;

  if (completedCount === totalSteps) return null;

  function handleDismiss() {
    localStorage.setItem(storageKey, "true");
    setDismissed(true);
  }

  return (
    <div className="rounded-lg border border-border bg-surface px-5 py-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-text-primary">Get set up</span>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:text-text-primary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="h-4 w-4"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-3">
        {steps.map((step, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="mt-0.5">
              {step.complete ? <CheckIcon /> : <EmptyCircleIcon />}
            </div>
            <div className="flex flex-col">
              <span
                className={
                  step.complete
                    ? "text-xs text-text-muted line-through"
                    : "text-xs text-text-primary"
                }
              >
                {step.label}
              </span>
              {!step.complete && step.ctaLabel && (
                step.onCta ? (
                  <button
                    onClick={step.onCta}
                    className="mt-0.5 w-fit border-0 bg-transparent p-0 text-xs text-accent hover:text-accent/80"
                  >
                    {step.ctaLabel} →
                  </button>
                ) : (
                  <span className="mt-0.5 text-xs text-text-muted">
                    {step.ctaLabel}
                  </span>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-text-muted">
        {completedCount} of {totalSteps} steps complete
      </p>
    </div>
  );
}
